/**
 * Labstep
 *
 * @module components/File/Image
 * @desc Component to show a file of image type
 */

import cx from 'classnames';
import { FileLightbox } from 'labstep-web/components/File/Lightbox';
import { CachedFileURLProvider } from 'labstep-web/containers/File/GetURL/Cached';
import { ThumbnailContainer } from 'labstep-web/containers/File/Thumbnail';
import { ToggleContainer } from 'labstep-web/containers/Toggle';
import type React from 'react';
import { Image, Placeholder } from 'semantic-ui-react';
import styles from './styles.module.scss';
import type {
  FileImageProps,
  FileImageToggleContainerProps,
} from './types';

/**
 * @desc Renders a file image
 */
export const FileImage: React.FC<FileImageProps> = ({
  file,
  expandable,
  toggle,
  toggled,
  className,
}) => {
  // For GIFs, use the original file URL to preserve animation
  if (file.isGif) {
    return (
      <CachedFileURLProvider file={file}>
        {({ url }) => (
          <>
            <div
              onClick={expandable ? toggle : undefined}
              className={styles.imageContainer}
            >
              <Image
                id={`image-${file.id}`}
                className={cx(
                  styles.image,
                  className,
                  'lr-hide-this',
                )}
                src={url}
                loading="lazy"
              />
            </div>
            {toggled && toggle && (
              <FileLightbox
                file={file}
                toggle={toggle}
                onError={() => {}}
              />
            )}
          </>
        )}
      </CachedFileURLProvider>
    );
  }

  // For other images, use the thumbnail system
  return (
    <ThumbnailContainer
      file={file.image_annotation ? file.image_annotation : file}
    >
      {({ src, status, onError }) => (
        <>
          <div
            onClick={
              expandable && status === 'loaded' ? toggle : undefined
            }
            className={styles.imageContainer}
          >
            {status === 'loading' ? (
              <Placeholder>
                <Placeholder.Image className={styles.placeholder} />
              </Placeholder>
            ) : (
              <Image
                id={`image-${file.id}`}
                className={cx(
                  styles.image,
                  className,
                  'lr-hide-this',
                )}
                src={src}
                onError={onError}
                loading="lazy"
              />
            )}
          </div>
          {toggled && toggle && (
            <FileLightbox
              file={file}
              toggle={toggle}
              onError={onError}
            />
          )}
        </>
      )}
    </ThumbnailContainer>
  );
};

FileImage.defaultProps = {
  expandable: true,
};

export const FileImageToggleContainer: React.FC<
  FileImageToggleContainerProps
> = (props) => (
  <ToggleContainer>
    {(containerProps) => <FileImage {...props} {...containerProps} />}
  </ToggleContainer>
);
